import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertCircle,
  Bell,
  BellOff,
  Check,
  ChevronDown,
  Loader2,
  Mail,
  Package,
  ShoppingCart,
  Smartphone,
  Wallet,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import Loader from '@/components/Loader/Loader';
import { Input } from '@/components/shared/Input/Input';
import { Button } from '@/components/ui/button';
import { SheetFooter, SheetTitle } from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import OnboardingSheet from '../../OnboardingSheet';

import {
  NOTIFICATION_CATEGORIES,
  NOTIFICATION_TYPE_LABELS,
  NotificationType,
} from '@/@types/Notifications';
import { useNotificationSettings } from './useNotificationSettings';

interface NotificationStepProps {
  open: boolean;
  onClose: () => void;
  canEdit?: boolean;
}

const categoryIcons: Record<string, React.ReactNode> = {
  sales: <ShoppingCart className="w-5 h-5" />,
  products: <Package className="w-5 h-5" />,
  withdrawals: <Wallet className="w-5 h-5" />,
};

const MotionDiv = motion.div;

export function NotificationStep({ open, onClose, canEdit = true }: NotificationStepProps) {
  const { t } = useTranslation(['onboarding', 'common']);
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({
    sales: true,
    products: false,
    withdrawals: false,
  });
  const [expandedItems, setExpandedItems] = useState<Record<NotificationType, boolean>>(
    {} as Record<NotificationType, boolean>
  );

  const {
    settings,
    isLoading,
    isPending,
    hasChanges,
    toggleGlobalEnabled,
    togglePreferenceEnabled,
    togglePreferenceChannel,
    setCustomEmail,
    saveChanges,
    resetChanges,
  } = useNotificationSettings({ onClose });

  const toggleCategory = (category: string) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const toggleItemExpand = (type: NotificationType) => {
    setExpandedItems((prev) => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  const handleClose = () => {
    if (hasChanges) {
      resetChanges();
    }
    onClose();
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 24,
      },
    },
  };

  return (
    <OnboardingSheet onClose={handleClose} open={open} hasHeader={false}>
      <div className="flex flex-col h-full">
        {isLoading ? (
          <div className="flex items-center justify-center flex-1">
            <Loader />
          </div>
        ) : (
          <>
            <div className="p-6 flex-1 overflow-y-auto">
              <SheetTitle>{t('notifications')}</SheetTitle>
              <p className="text-muted text-sm mt-2 mb-4">{t('notifications_description')}</p>

              <div className="py-2">
                <div className="flex items-start gap-4 p-4 rounded-lg bg-elevated border border-colorBorder">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                    {settings?.enabled ? (
                      <Bell className="w-5 h-5 text-primary" />
                    ) : (
                      <BellOff className="w-5 h-5 text-muted" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-4 mb-1">
                      <h3 className="font-semibold text-base">
                        {t('enable_notifications')}
                      </h3>
                      <Switch
                        checked={settings?.enabled}
                        onCheckedChange={toggleGlobalEnabled}
                        className="data-[state=checked]:bg-primary"
                        disabled={!canEdit}
                      />
                    </div>
                    <p className="text-sm text-muted">
                      {settings?.enabled
                        ? t('notifications_enabled_description')
                        : t('notifications_disabled_description')}
                    </p>
                  </div>
                </div>
              </div>

              <AnimatePresence mode="wait">
                {settings?.enabled ? (
                  <MotionDiv
                    key="notification-content"
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    className="space-y-3"
                  >
                    {Object.entries(NOTIFICATION_CATEGORIES).map(([category, types]) => (
                      <MotionDiv key={category} variants={itemVariants}>
                        <div className="border border-colorBorder rounded-lg bg-card overflow-hidden">
                          <button
                            type="button"
                            onClick={() => toggleCategory(category)}
                            className="w-full p-4 flex items-center justify-between hover:bg-elevated/50 transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              {categoryIcons[category]}
                              <div className="text-left">
                                <h3 className="font-semibold text-base">
                                  {t(`category_${category}`)}
                                </h3>
                                <p className="text-sm text-muted">
                                  {types.length} {t('notification_types')}
                                </p>
                              </div>
                            </div>
                            <ChevronDown
                              className={cn(
                                'w-5 h-5 text-muted transition-transform duration-200',
                                expandedCategories[category] && 'rotate-180'
                              )}
                            />
                          </button>

                          <AnimatePresence>
                            {expandedCategories[category] && (
                              <MotionDiv
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.2 }}
                              >
                                <div className="border-t border-colorBorder divide-y divide-colorBorder">
                                  {types.map((type) => {
                                    const preference = settings?.preferences[type];
                                    const isExpanded = expandedItems[type];

                                    return (
                                      <div key={type} className="p-4">
                                        <div className="flex items-center justify-between mb-3">
                                          <div className="flex items-center gap-3 flex-1">
                                            <Switch
                                              checked={preference?.enabled}
                                              onCheckedChange={() =>
                                                togglePreferenceEnabled(type)
                                              }
                                              className="data-[state=checked]:bg-primary"
                                              disabled={!canEdit}
                                            />
                                            <span className="font-medium text-sm">
                                              {t(NOTIFICATION_TYPE_LABELS[type])
                                                .replace('Venda concluída', 'Venda realizada')
                                                .replace('Pedido reembolsado', 'Venda reembolsada')}
                                            </span>
                                          </div>
                                          {preference?.enabled && (
                                            <div className="flex items-center gap-2">
                                              {preference.channels.includes('email') && (
                                                <Mail className="w-4 h-4 text-primary" />
                                              )}
                                              {preference.channels.includes('push') && (
                                                <Smartphone className="w-4 h-4 text-primary" />
                                              )}
                                              <button
                                                type="button"
                                                onClick={() => toggleItemExpand(type)}
                                                className="p-1.5 rounded-lg hover:bg-elevated transition-colors"
                                              >
                                                <ChevronDown
                                                  className={cn(
                                                    'w-4 h-4 transition-transform duration-200',
                                                    isExpanded && 'rotate-180'
                                                  )}
                                                />
                                              </button>
                                            </div>
                                          )}
                                        </div>

                                        <AnimatePresence>
                                          {preference?.enabled && isExpanded && (
                                            <MotionDiv
                                              initial={{ height: 0, opacity: 0 }}
                                              animate={{ height: 'auto', opacity: 1 }}
                                              exit={{ height: 0, opacity: 0 }}
                                              transition={{ duration: 0.2 }}
                                              className="space-y-4 pt-2 border-t border-colorBorder"
                                            >
                                              <div>
                                                <p className="text-sm font-medium mb-3">
                                                  {t('select_channels')}
                                                </p>
                                                <div className="flex gap-2">
                                                  <button
                                                    type="button"
                                                    onClick={() =>
                                                      togglePreferenceChannel(type, 'email')
                                                    }
                                                    disabled={!canEdit}
                                                    className={cn(
                                                      'flex items-center gap-2 px-3 py-2 rounded-lg border transition-all duration-200',
                                                      preference.channels.includes('email')
                                                        ? 'bg-primary/20 border-primary/30 text-primary'
                                                        : 'bg-elevated border-colorBorder text-muted hover:border-primary/30'
                                                    )}
                                                  >
                                                    <Mail className="w-4 h-4" />
                                                    Email
                                                    {preference.channels.includes('email') && (
                                                      <Check className="w-4 h-4" />
                                                    )}
                                                  </button>
                                                  <button
                                                    type="button"
                                                    onClick={() =>
                                                      togglePreferenceChannel(type, 'push')
                                                    }
                                                    disabled={!settings?.has_mobile_token || !canEdit}
                                                    className={cn(
                                                      'flex items-center gap-2 px-3 py-2 rounded-lg border transition-all duration-200',
                                                      preference.channels.includes('push')
                                                        ? 'bg-primary/20 border-primary/30 text-primary'
                                                        : 'bg-elevated border-colorBorder text-muted hover:border-primary/30',
                                                      !settings?.has_mobile_token &&
                                                        'opacity-50 cursor-not-allowed'
                                                    )}
                                                  >
                                                    <Smartphone className="w-4 h-4" />
                                                    Push
                                                    {preference.channels.includes('push') && (
                                                      <Check className="w-4 h-4" />
                                                    )}
                                                  </button>
                                                </div>
                                                {!settings?.has_mobile_token && (
                                                  <div className="flex items-start gap-2 mt-2 text-xs text-warning">
                                                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                                    <span>{t('push_unavailable')}</span>
                                                  </div>
                                                )}
                                              </div>

                                              {preference.channels.includes('email') && (
                                                <div>
                                                  <label className="text-sm font-medium mb-2 block">
                                                    {t('custom_email_label')}
                                                  </label>
                                                  <Input
                                                    type="email"
                                                    placeholder="exemplo@email.com"
                                                    value={preference.custom_email || ''}
                                                    onChange={(e) =>
                                                      setCustomEmail(
                                                        type,
                                                        e.target.value || null
                                                      )
                                                    }
                                                    className="h-9 text-sm"
                                                    disabled={!canEdit}
                                                  />
                                                  <p className="text-xs text-muted mt-1.5">
                                                    {t('custom_email_hint')}
                                                  </p>
                                                </div>
                                              )}
                                            </MotionDiv>
                                          )}
                                        </AnimatePresence>
                                      </div>
                                    );
                                  })}
                                </div>
                              </MotionDiv>
                            )}
                          </AnimatePresence>
                        </div>
                      </MotionDiv>
                    ))}
                  </MotionDiv>
                ) : (
                  <MotionDiv
                    key="notification-disabled"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="px-6 text-center py-12"
                  >
                    <div className="w-16 h-16 rounded-full bg-elevated mx-auto mb-4 flex items-center justify-center">
                      <BellOff className="w-8 h-8 text-muted" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">
                      {t('notifications_off_title')}
                    </h3>
                    <p className="text-sm text-muted max-w-md mx-auto">
                      {t('notifications_off_description')}
                    </p>
                  </MotionDiv>
                )}
              </AnimatePresence>
            </div>

            <SheetFooter className="px-6 py-4 border-t border-colorBorder">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isPending}
              >
                {t('cancel')}
              </Button>
              <Button
                type="button"
                onClick={saveChanges}
                disabled={!canEdit || isPending || !hasChanges}
              >
                {isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('saving')}
                  </>
                ) : (
                  t('save')
                )}
              </Button>
            </SheetFooter>
          </>
        )}
      </div>
    </OnboardingSheet>
  );
}
